/**
 * Feeds management module
 * Handles the Feeds tab functionality
 */

import { API } from '../core/api.js';
import { showNotification } from '../components/notifications.js';
import { escapeHtml, stripHtml } from '../core/utils.js';

// Cache for available models
let availableModels = [];

/**
 * Load LLM settings and available models
 */
export async function loadLLMSettings() {
    const container = document.getElementById('llm-settings-content');
    if (!container) return;

    try {
        // Fetch settings and available models in parallel
        const [settings, modelsData] = await Promise.all([
            API.get('/api/feeds/llm-settings'),
            API.get('/api/llm/models')
        ]);

        availableModels = modelsData.models || [];
        renderLLMSettings(container, settings, availableModels);
    } catch (error) {
        console.error('Error loading LLM settings:', error);
        container.textContent = '';
        const errorP = document.createElement('p');
        errorP.style.color = 'var(--error)';
        errorP.textContent = 'Failed to load LLM settings';
        container.appendChild(errorP);
    }
}

/**
 * Create a select element with model options
 */
function createModelSelect(id, models, includeNone = false) {
    const select = document.createElement('select');
    select.id = id;
    select.style.cssText = 'padding: 0.5rem; border: 1px solid var(--border); border-radius: 4px; background: var(--bg-primary); color: var(--text-primary); min-width: 200px;';

    if (includeNone) {
        const noneOption = document.createElement('option');
        noneOption.value = '';
        noneOption.textContent = '(None - use only primary)';
        select.appendChild(noneOption);
    }

    models.forEach(m => {
        const option = document.createElement('option');
        option.value = m.id;
        option.textContent = m.name;
        select.appendChild(option);
    });

    return select;
}

/**
 * Create a settings row
 */
function createSettingsRow(labelText, inputElement, hintText) {
    const row = document.createElement('div');
    row.style.cssText = 'display: flex; align-items: center; gap: 1rem; margin-bottom: 1rem; flex-wrap: wrap;';

    const label = document.createElement('label');
    label.style.cssText = 'color: var(--text-secondary); font-size: 0.9rem; min-width: 120px;';
    label.textContent = labelText;
    row.appendChild(label);

    row.appendChild(inputElement);

    if (hintText) {
        const hint = document.createElement('span');
        hint.style.cssText = 'color: var(--text-muted); font-size: 0.8rem;';
        hint.textContent = hintText;
        row.appendChild(hint);
    }

    return row;
}

/**
 * Render LLM settings UI with dropdowns
 */
function renderLLMSettings(container, settings, models) {
    container.textContent = '';

    // Default model dropdown
    const defaultModelSelect = createModelSelect('llm-default-model', models);
    defaultModelSelect.value = settings.default_model || '';
    container.appendChild(createSettingsRow('Default Model:', defaultModelSelect, 'Used for chat & individual analysis'));

    // Batch model 1 dropdown
    const batchModel1Select = createModelSelect('llm-batch-model-1', models);
    if (settings.batch_models?.[0]) {
        batchModel1Select.value = settings.batch_models[0];
    }
    container.appendChild(createSettingsRow('Batch Model 1:', batchModel1Select, 'Primary model for batch processing'));

    // Batch model 2 dropdown (optional)
    const batchModel2Select = createModelSelect('llm-batch-model-2', models, true);
    if (settings.batch_models?.[1]) {
        batchModel2Select.value = settings.batch_models[1];
    }
    container.appendChild(createSettingsRow('Batch Model 2:', batchModel2Select, 'Secondary model (cycles with primary)'));

    // Temperature input
    const tempInput = document.createElement('input');
    tempInput.type = 'number';
    tempInput.id = 'llm-temperature';
    tempInput.min = '0';
    tempInput.max = '1';
    tempInput.step = '0.1';
    tempInput.value = settings.temperature;
    tempInput.style.cssText = 'padding: 0.5rem; border: 1px solid var(--border); border-radius: 4px; background: var(--bg-primary); color: var(--text-primary); width: 80px;';
    container.appendChild(createSettingsRow('Temperature:', tempInput, null));

    // Max tokens input
    const tokensInput = document.createElement('input');
    tokensInput.type = 'number';
    tokensInput.id = 'llm-max-tokens';
    tokensInput.min = '256';
    tokensInput.max = '16384';
    tokensInput.step = '256';
    tokensInput.value = settings.max_tokens;
    tokensInput.style.cssText = 'padding: 0.5rem; border: 1px solid var(--border); border-radius: 4px; background: var(--bg-primary); color: var(--text-primary); width: 100px;';
    container.appendChild(createSettingsRow('Max Tokens:', tokensInput, null));

    // Save button row
    const buttonRow = document.createElement('div');
    buttonRow.style.marginTop = '1rem';

    const saveBtn = document.createElement('button');
    saveBtn.id = 'save-llm-settings';
    saveBtn.className = 'btn btn-primary';
    saveBtn.style.marginRight = '0.5rem';
    saveBtn.textContent = '💾 Save Settings';
    saveBtn.addEventListener('click', saveLLMSettings);
    buttonRow.appendChild(saveBtn);

    const statusSpan = document.createElement('span');
    statusSpan.id = 'llm-save-status';
    statusSpan.style.cssText = 'color: var(--text-muted); font-size: 0.9rem;';
    buttonRow.appendChild(statusSpan);

    container.appendChild(buttonRow);
}

/**
 * Save LLM settings
 */
async function saveLLMSettings() {
    const saveBtn = document.getElementById('save-llm-settings');
    const statusSpan = document.getElementById('llm-save-status');

    const defaultModel = document.getElementById('llm-default-model')?.value;
    const batchModel1 = document.getElementById('llm-batch-model-1')?.value;
    const batchModel2 = document.getElementById('llm-batch-model-2')?.value;
    const temperature = document.getElementById('llm-temperature')?.value;
    const maxTokens = document.getElementById('llm-max-tokens')?.value;

    // Build batch models list
    const batchModels = [batchModel1];
    if (batchModel2) {
        batchModels.push(batchModel2);
    }

    saveBtn.disabled = true;
    saveBtn.textContent = '⏳ Saving...';

    try {
        const formData = new FormData();
        if (defaultModel) formData.append('default_model', defaultModel);
        if (batchModels.length > 0) formData.append('batch_models', batchModels.join(','));
        if (temperature) formData.append('temperature', temperature);
        if (maxTokens) formData.append('max_tokens', maxTokens);

        const response = await fetch('/api/feeds/llm-settings', {
            method: 'PUT',
            body: formData
        });

        if (response.ok) {
            saveBtn.textContent = '✅ Saved!';
            statusSpan.textContent = 'Settings saved successfully';
            statusSpan.style.color = 'var(--success)';
            showNotification('LLM settings saved', 'success');
        } else {
            const error = await response.json();
            throw new Error(error.detail || 'Failed to save');
        }
    } catch (error) {
        console.error('Error saving LLM settings:', error);
        saveBtn.textContent = '❌ Error';
        statusSpan.textContent = error.message;
        statusSpan.style.color = 'var(--error)';
        showNotification('Failed to save LLM settings', 'error');
    }

    setTimeout(() => {
        saveBtn.disabled = false;
        saveBtn.textContent = '💾 Save Settings';
    }, 2000);
}

/**
 * Load feeds list
 */
export async function loadFeeds() {
    try {
        const data = await API.get('/api/feeds');

        const container = document.getElementById('feeds-list');
        if (!container) return;

        if (data.feeds.length === 0) {
            container.textContent = '';
            const msg = document.createElement('p');
            msg.style.color = 'var(--text-secondary)';
            msg.textContent = 'No feeds configured. Edit feeds.yaml to add feeds.';
            container.appendChild(msg);
            return;
        }

        // Group feeds by category
        const feedsByCategory = {};
        data.feeds.forEach(feed => {
            const category = feed.main_category || 'uncategorized';
            if (!feedsByCategory[category]) {
                feedsByCategory[category] = [];
            }
            feedsByCategory[category].push(feed);
        });

        // Clear and rebuild
        container.textContent = '';

        for (const [category, feeds] of Object.entries(feedsByCategory)) {
            const section = document.createElement('div');
            section.style.marginBottom = '2rem';

            const heading = document.createElement('h3');
            heading.style.marginBottom = '1rem';
            heading.style.textTransform = 'capitalize';
            heading.textContent = category;
            section.appendChild(heading);

            for (const feed of feeds) {
                const card = createFeedCard(feed);
                section.appendChild(card);
            }

            container.appendChild(section);
        }
    } catch (error) {
        console.error('Error loading feeds:', error);
    }
}

/**
 * Create a feed card element
 */
function createFeedCard(feed) {
    const card = document.createElement('div');
    card.className = 'feed-card';
    if (feed.disabled) {
        card.style.opacity = '0.6';
    }

    // Header with title and controls
    const header = document.createElement('div');
    header.className = 'feed-header';

    // Left side - title and info
    const info = document.createElement('div');

    const title = document.createElement('div');
    title.className = 'feed-title';
    title.textContent = feed.name;
    if (feed.disabled) {
        const disabledBadge = document.createElement('span');
        disabledBadge.style.cssText = 'color: var(--error); margin-left: 0.5rem; font-size: 0.85rem;';
        disabledBadge.textContent = '(Disabled)';
        title.appendChild(disabledBadge);
    }
    info.appendChild(title);

    const url = document.createElement('div');
    url.className = 'feed-url';
    url.textContent = feed.feed_url;
    info.appendChild(url);

    if (feed.description) {
        const desc = document.createElement('p');
        desc.style.cssText = 'color: var(--text-secondary); margin-top: 0.5rem;';
        desc.textContent = stripHtml(feed.description);
        info.appendChild(desc);
    }

    header.appendChild(info);

    // Right side - enable/disable toggle
    const controls = document.createElement('div');
    controls.className = 'feed-controls';
    controls.style.display = 'flex';
    controls.style.alignItems = 'center';
    controls.style.gap = '1rem';

    // Enable/Disable toggle
    const enableLabel = document.createElement('label');
    enableLabel.className = 'toggle-switch';
    enableLabel.title = feed.disabled ? 'Enable feed' : 'Disable feed';

    const enableCheckbox = document.createElement('input');
    enableCheckbox.type = 'checkbox';
    enableCheckbox.checked = !feed.disabled;
    enableCheckbox.addEventListener('change', () => {
        updateFeedSettings(feed.feed_url, { disabled: !enableCheckbox.checked });
    });

    const slider = document.createElement('span');
    slider.className = 'toggle-slider';

    enableLabel.appendChild(enableCheckbox);
    enableLabel.appendChild(slider);
    controls.appendChild(enableLabel);

    header.appendChild(controls);
    card.appendChild(header);

    // Settings row
    const settings = document.createElement('div');
    settings.style.cssText = 'display: flex; gap: 1.5rem; margin-top: 1rem; align-items: center; flex-wrap: wrap;';

    // Max episodes input
    const maxEpisodesGroup = document.createElement('div');
    maxEpisodesGroup.style.cssText = 'display: flex; align-items: center; gap: 0.5rem;';

    const maxLabel = document.createElement('span');
    maxLabel.style.cssText = 'color: var(--text-secondary); font-size: 0.85rem;';
    maxLabel.textContent = 'Max episodes:';

    const maxInput = document.createElement('input');
    maxInput.type = 'number';
    maxInput.min = '1';
    maxInput.max = '10000';
    maxInput.value = feed.max_episodes || 5;
    maxInput.style.cssText = 'width: 70px; padding: 0.25rem 0.5rem; border: 1px solid var(--border); border-radius: 4px; background: var(--bg-secondary); color: var(--text-primary);';
    maxInput.addEventListener('change', () => {
        const val = parseInt(maxInput.value);
        if (val > 0) {
            updateFeedSettings(feed.feed_url, { max_episodes: val });
        }
    });

    maxEpisodesGroup.appendChild(maxLabel);
    maxEpisodesGroup.appendChild(maxInput);
    settings.appendChild(maxEpisodesGroup);

    // Auto-download toggle
    const autoDownloadGroup = document.createElement('div');
    autoDownloadGroup.style.cssText = 'display: flex; align-items: center; gap: 0.5rem;';

    const autoLabel = document.createElement('span');
    autoLabel.style.cssText = 'color: var(--text-secondary); font-size: 0.85rem;';
    autoLabel.textContent = 'Auto-download:';

    const autoCheckbox = document.createElement('input');
    autoCheckbox.type = 'checkbox';
    autoCheckbox.checked = feed.auto_download;
    autoCheckbox.style.cssText = 'width: 18px; height: 18px; cursor: pointer;';
    autoCheckbox.addEventListener('change', () => {
        updateFeedSettings(feed.feed_url, { auto_download: autoCheckbox.checked });
    });

    autoDownloadGroup.appendChild(autoLabel);
    autoDownloadGroup.appendChild(autoCheckbox);
    settings.appendChild(autoDownloadGroup);

    // Priority badge
    const priority = document.createElement('span');
    priority.style.cssText = 'color: var(--text-secondary); font-size: 0.85rem;';
    priority.textContent = `Priority: ${feed.priority || 'normal'}`;
    settings.appendChild(priority);

    // Category badge
    if (feed.category) {
        const categoryBadge = document.createElement('span');
        categoryBadge.style.cssText = 'color: var(--text-secondary); font-size: 0.85rem;';
        categoryBadge.textContent = `Category: ${feed.category}`;
        settings.appendChild(categoryBadge);
    }

    card.appendChild(settings);

    return card;
}

/**
 * Update feed settings
 */
async function updateFeedSettings(feedUrl, settings) {
    try {
        const formData = new FormData();
        formData.append('feed_url', feedUrl);

        if (settings.disabled !== undefined) {
            formData.append('disabled', settings.disabled);
        }
        if (settings.max_episodes !== undefined) {
            formData.append('max_episodes', settings.max_episodes);
        }
        if (settings.auto_download !== undefined) {
            formData.append('auto_download', settings.auto_download);
        }

        const response = await fetch('/api/feeds/settings', {
            method: 'PUT',
            body: formData
        });

        if (response.ok) {
            showNotification('Feed settings updated', 'success');
            // Reload to reflect changes
            loadFeeds();
        } else {
            const error = await response.json();
            showNotification(error.detail || 'Failed to update settings', 'error');
        }
    } catch (error) {
        console.error('Error updating feed settings:', error);
        showNotification('Error updating feed settings', 'error');
    }
}

/**
 * Reload feeds from YAML config
 */
export async function reloadFeeds(buttonElement) {
    const button = buttonElement || event?.target;
    if (!button) return;

    button.disabled = true;
    button.textContent = '⏳ Reloading...';

    try {
        await fetch('/api/feeds/reload', { method: 'POST' });

        button.textContent = '✅ Reloaded!';
        setTimeout(() => {
            button.textContent = '🔄 Reload Feeds';
            button.disabled = false;
            loadFeeds();
        }, 2000);
    } catch (error) {
        console.error('Error reloading feeds:', error);
        button.textContent = '❌ Error';
        setTimeout(() => {
            button.textContent = '🔄 Reload Feeds';
            button.disabled = false;
        }, 2000);
    }
}

/**
 * Toggle feed auto-download setting (legacy, kept for compatibility)
 */
export async function toggleFeedAutoDownload(feedUrl, autoDownload) {
    await updateFeedSettings(feedUrl, { auto_download: autoDownload });
}
